//angular
import { Platform } from "@angular/cdk/platform";
import { Injectable } from "@angular/core";

//Msal
import {
    LogLevel,
    ProtocolMode,
    IPublicClientApplication,
    PublicClientApplication,
    InteractionType,
    Configuration,
} from '@azure/msal-browser';

import {
    MsalInterceptorConfiguration,
    MsalInterceptorAuthRequest,
    MsalGuardConfiguration,
    ProtectedResourceScopes
} from '@azure/msal-angular';

//local
import { environment } from '@env/environment';
import { ApplicationConfiguration } from "@core/configuration";
import { securityLog } from '../helpers';
import { AuthRequestBuilderService } from './auth-request-builder.service';
import { B2cAuthorityService } from './b2c-authority.service';
import { MsalStoragePolicyService } from './msal-storage-policy.service';
import { ProtectedResourceService } from './protected-resource.service';

@Injectable({
    providedIn: 'root'
})
export class MsalConfigService {
    public readonly msalConfig: Configuration;
    
    constructor(
        private applicationConfig: ApplicationConfiguration,
        private platform: Platform,
        private authRequestBuilder: AuthRequestBuilderService,
        private authorityService: B2cAuthorityService,
        private protectedResourceService: ProtectedResourceService,
        private storagePolicy: MsalStoragePolicyService) {
        this.msalConfig = {
            auth: {
                clientId: this.applicationConfig.appId, // This is the ONLY mandatory field that you need to supply.

                //b2cPolicies.authorities.signUpSignIn.authority
                // Defaults to "https://login.microsoftonline.com/common"
                authority: this.authorityService.signUpSignInAuthority,
                protocolMode: ProtocolMode.AAD,
                knownAuthorities: [this.applicationConfig.azureB2c.instance], // Mark your B2C tenant's domain as trusted.
                redirectUri: "/auth", // Points to window.location.origin by default. You must register this URI on Azure portal/App Registration.
                postLogoutRedirectUri: "/", // Points to window.location.origin by default.
                //navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.

            },
            cache: {
                cacheLocation: this.storagePolicy.cacheLocation, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
                storeAuthStateInCookie: this.platform.TRIDENT, // Set this to "true" if you are having issues on IE11 or Edge. Remove this line to use Angular Universal
            },
            system: {
                /**
                 * Below you can configure MSAL.js logs. For more information, visit:
                 * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
                 */
                //allowNativeBroker: false, // Disables WAM Broker
                loggerOptions: {
                    loggerCallback(logLevel: LogLevel, message: string) {

                        switch (logLevel) {
                            case LogLevel.Error:
                                securityLog.error(message);
                                return;
                            case LogLevel.Info:
                                securityLog.info(message);
                                return;
                            case LogLevel.Verbose:
                                securityLog.debug(message);
                                return;
                            case LogLevel.Warning:
                                securityLog.warn(message);
                                return;
                        }
                    },
                    logLevel: environment.production ? LogLevel.Warning : LogLevel.Verbose,//TODO:DIW:Allow config
                    piiLoggingEnabled: false
                }
            }

        };
    }

    /**
     * Here we pass the configuration parameters to create an MSAL instance.
     * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
     */
    public MSALInstanceFactory(): IPublicClientApplication {
        return new PublicClientApplication(this.msalConfig);
    }


    /**
     * MSAL Angular will automatically retrieve tokens for resources
     * added to protectedResourceMap. For more info, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
     */
    public MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
        const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();

        let protectedResources = this.protectedResourceService.getProtectedResources();

        protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
        protectedResourceMap.set(`${this.msalConfig.auth.authority}/openid/v2.0/userinfo`, [...protectedResources.applicationApi.scopes.read]);

        protectedResourceMap.set(protectedResources.applicationApi.endpoint,
            [
                {
                    httpMethod: 'GET',
                    scopes: [...protectedResources.applicationApi.scopes.read]
                },
                {
                    httpMethod: 'POST',
                    scopes: [...protectedResources.applicationApi.scopes.write]
                },
                {
                    httpMethod: 'PUT',
                    scopes: [...protectedResources.applicationApi.scopes.write]
                },
                {
                    httpMethod: 'DELETE',
                    scopes: [...protectedResources.applicationApi.scopes.write]
                },
                {
                    httpMethod: 'PATCH',
                    scopes: [...protectedResources.applicationApi.scopes.write]
                }            
            ]);

        return {
            interactionType: InteractionType.Redirect,
            protectedResourceMap,
            authRequest: (): MsalInterceptorAuthRequest => {
                return this.authRequestBuilder.createRequestMetadata();
            },
        };
    }

    /**
     * Set your default interaction type for MSALGuard here. If you have any
     * additional scopes you want the user to consent upon login, add them here as well.
     */
    public MSALGuardConfigFactory(): MsalGuardConfiguration {
        const protectedScopes = this.protectedResourceService.getProtectedResources();

        return {
            interactionType: InteractionType.Redirect,
            authRequest: () => this.authRequestBuilder.createLoginRedirectRequest(
                protectedScopes.applicationApi.scopes.read,
                protectedScopes.applicationApi.scopes.write,
            ),
            loginFailedRoute: '/login-failed'
        };
    }

}
